import React from 'react';
import { Button, ButtonGroup, Icon, IconButton, InlineFeedback, Menu, Tag, TextButton } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import StringConstants from '@common/constants/StringConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import TransactionConstants from '@common/constants/TransactionConstants';
import StyledToolTip from '@rental-manager/components/StyledTooltip';
import DepositDateTooltipComponent from '@rental-manager/components/StyledTooltip/DepositDateToolTip';
import type { ITransactionModel } from '@rental-manager/models/Types';
import { getFirstName } from '@rental-manager/models/PropertyInfo';
import RentalProtectionPremiumRow from './RentalProtectionPremiumRow';
import MarkAsPaidAction from './MarkAsPaidAction';
import OfflineMarkNote from './OfflineMarkNote';
import UndoOfflineMarkAction from './UndoOfflineMarkAction';
import ExpansionPanel from '@zg-rentals/multibrand-ui/lib/ExpansionPanel';

type Props = {
  transaction: ITransactionModel;
  userEvent: Record<string, any>;
  resendInviteToRenter: ({ tenantEmail }) => void;
  requestRemindRenterPayRent: ({ tenantEmail }) => void;
  listingAlias: string;
  goToSetupPaymentPlan: () => void;
  paymentPlanEnabled: boolean;
};

class RentPaymentsRow extends React.Component<Props> {
  state = {
    expandedRentalProtectionCompletedSection: false,
  };
  toggleRentalProtectionCompleted = (): void => {
    this.setState({
      expandedRentalProtectionCompletedSection: !this.state.expandedRentalProtectionCompletedSection,
    });
  };

  getStatusText = (): React.ReactNode => {
    const { returnInitiatedDate, landlordStatus, dueDate, dateReceived } = this.props.transaction;
    const returnStartedOn = formatDate(returnInitiatedDate || dueDate, 'M/d/yy');
    const TooltipComponent = (
      <StyledToolTip className="Dashboard-ledger-tooltip" iconName="help-circle-fill">
        <Text>
          Payments may be returned for reasons including overpayment, a payout after a dispute, or payment failure due
          to insufficient funds. Returned payments incur a small fee. Questions?&nbsp;
          <Link target="_blank" href={StringConstants.LINKS.SUPPORT_PAGE.LANDLORD}>
            Contact us
          </Link>
        </Text>
      </StyledToolTip>
    );
    switch (landlordStatus) {
      case TransactionConstants.STATUS.PENDING:
        return (
          <Text className="Expected fs-exclude">
            Deposit date: {formatDate(dateReceived, 'M/d/yy')}
            <DepositDateTooltipComponent />
          </Text>
        );
      case TransactionConstants.STATUS.COMPLETE:
        return <Text className="fs-exclude">Deposited: {formatDate(dateReceived, 'M/d/yy')}</Text>;
      case TransactionConstants.STATUS.REFUNDED:
        return (
          <>
            <Text className="fs-exclude">Return started {returnStartedOn}</Text>
            <Text className="fs-exclude" inline>
              Reason: Refunded{TooltipComponent}
            </Text>
          </>
        );
      case TransactionConstants.STATUS.DISPUTED:
        return (
          <>
            <Text className="fs-exclude">Return started {returnStartedOn}</Text>
            <Text className="fs-exclude" inline>
              Reason: Disputed{TooltipComponent}
            </Text>
          </>
        );
      case TransactionConstants.STATUS.PAYMENT_FAILED:
      case TransactionConstants.STATUS.DEPOSIT_FAILED:
        return (
          <>
            <Text className="fs-exclude">Return started {returnStartedOn}</Text>
            <Text className="fs-exclude" inline>
              Reason: Failed{TooltipComponent}
            </Text>
          </>
        );
      case TransactionConstants.STATUS.UNPAID:
      case TransactionConstants.STATUS.PAST_DUE:
      default:
        return '';
    }
  };

  getPaymentSent = (): React.ReactNode => {
    const { landlordStatus, dateCreated } = this.props.transaction;
    if (
      landlordStatus === TransactionConstants.STATUS.PENDING ||
      landlordStatus === TransactionConstants.STATUS.COMPLETE
    ) {
      return <Text className="Inititated fs-exclude">Payment sent: {formatDate(dateCreated, 'M/d/yy')}</Text>;
    } else {
      return <></>;
    }
  };

  getBankType = (): string => {
    const { paymentCategory, accountType, landlordStatus } = this.props.transaction;
    if (paymentCategory === 'upcoming' || landlordStatus === TransactionConstants.STATUS.MANUAL) {
      return '';
    }
    switch (accountType) {
      case 'credit':
        return 'Credit card';
      case 'debit':
        return 'Debit card';
      case 'bank_account':
        return 'ACH transfer';
      default:
        return '';
    }
  };

  resendInviteToRenter = (): unknown => {
    const tenantEmail = this.props.transaction.tenant.email;
    return this.props.resendInviteToRenter({ tenantEmail });
  };

  requestRemindRenterPayRent = (): unknown => {
    const tenantEmail = this.props.transaction.tenant.email;
    return this.props.requestRemindRenterPayRent({ tenantEmail });
  };

  renderLeftColumn = (): React.ReactNode => {
    const { userEvent, transaction, goToSetupPaymentPlan, paymentPlanEnabled } = this.props;
    const {
      tenant,
      paymentCategory,
      showPaidOffline,
      showResendInvitation,
      showRemindRenter,
      rentMonthHeading,
      dateReceived,
    } = this.props.transaction;
    const showAccountType = paymentCategory === 'completed';
    const markAsPaidEnabled = getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false);
    const isPastDue = transaction.ledgerGroup === TransactionConstants.LEDGER_GROUP.PAST_DUE;
    const showMarkAsPaid = markAsPaidEnabled && isPastDue;
    const showPaymentPlan = paymentPlanEnabled && isPastDue;
    const showAnyPaymentAction = showMarkAsPaid || showRemindRenter || showPaymentPlan;
    const awaitingAutopay = showMarkAsPaid && Boolean(transaction.awaitingAutopay);
    return (
      <Row>
        <Col>
          <Text className="fs-exclude" emphasize>
            {rentMonthHeading}
          </Text>
          {this.getStatusText()}
          <Text className="fs-exclude">{tenant.name}</Text>
          {showAccountType && <Text className="fs-exclude">{this.getBankType()}</Text>}
          {this.getPaymentSent()}
          {showPaidOffline && (
            <Text className="fs-exclude">{markAsPaidEnabled && dateReceived ? 'Marked as paid' : 'Paid offline'}</Text>
          )}
          {showPaidOffline && markAsPaidEnabled && Boolean(dateReceived) && (
            <Text className="fs-exclude">Received: {formatDate(dateReceived, 'M/d/yy')}</Text>
          )}
          {showPaidOffline && markAsPaidEnabled && Boolean(dateReceived) && transaction.manualPaymentNotes && (
            <OfflineMarkNote note={transaction.manualPaymentNotes} />
          )}
          {showPaidOffline && markAsPaidEnabled && Boolean(dateReceived) && (
            <UndoOfflineMarkAction
              propertyId={transaction.propertyId}
              notes={transaction.notes}
              target={{
                kind: 'rent',
                rentTermsKey: transaction.rentTermsKey,
                scheduleMonth: transaction.scheduleMonth,
              }}
            />
          )}
          {userEvent.message && (
            <Text type="body-tiny" className="Dashboard-userEventMessage pvs fs-exclude">
              {userEvent.message}
            </Text>
          )}
          {showResendInvitation && (
            <Text>
              <Link onClick={this.resendInviteToRenter}>Resend Invitation</Link>
            </Text>
          )}
          {showAnyPaymentAction &&
            (showMarkAsPaid ? (
              <ButtonGroup aria-label="Payment actions">
                <MarkAsPaidAction
                  heading={rentMonthHeading}
                  subheading={`Due ${formatDate(transaction.dueDate, 'MMM d, yyyy')} · ${tenant.name}`}
                  amount={centsToDollars(transaction.rentAmount, { style: 'effectiveShort' })}
                  propertyId={transaction.propertyId}
                  target={{
                    kind: 'rent',
                    rentTermsKey: transaction.rentTermsKey,
                    scheduleMonth: transaction.scheduleMonth,
                  }}
                  disabled={awaitingAutopay}
                />
                {(showRemindRenter || showPaymentPlan) && (
                  <Menu
                    trigger={
                      <IconButton size="sm" tone="brand" emphasis="outlined" title="More payment actions">
                        <Icon>
                          <IconMoreFilled />
                        </Icon>
                      </IconButton>
                    }
                  >
                    {showRemindRenter && (
                      <Menu.Item onClick={this.requestRemindRenterPayRent}>
                        <Menu.ItemLabel>Remind {getFirstName(tenant.name)}</Menu.ItemLabel>
                      </Menu.Item>
                    )}
                    {showPaymentPlan && (
                      <Menu.Item onClick={goToSetupPaymentPlan}>
                        <Menu.ItemLabel>Set up payment plan</Menu.ItemLabel>
                      </Menu.Item>
                    )}
                  </Menu>
                )}
              </ButtonGroup>
            ) : (
              <ButtonGroup aria-label="Payment actions">
                {showRemindRenter && (
                  <Button size="sm" onClick={this.requestRemindRenterPayRent}>
                    Remind {getFirstName(tenant.name)}
                  </Button>
                )}
                {showPaymentPlan &&
                  (showRemindRenter ? (
                    <TextButton onClick={goToSetupPaymentPlan}>Set up payment plan</TextButton>
                  ) : (
                    <Button size="sm" onClick={goToSetupPaymentPlan}>
                      Set up payment plan
                    </Button>
                  ))}
              </ButtonGroup>
            ))}
          {awaitingAutopay && (
            <InlineFeedback tone="info" css={{ marginBlockStart: 'xs' }}>
              Autopay is scheduled to collect this rent — you can mark it as paid if that payment fails
            </InlineFeedback>
          )}
        </Col>
      </Row>
    );
  };

  renderRightColumn = (): React.ReactNode => {
    const { landlordStatus, rentAmount } = this.props.transaction;
    const showReturnedBadge =
      landlordStatus === TransactionConstants.STATUS.REFUNDED ||
      landlordStatus === TransactionConstants.STATUS.DISPUTED ||
      landlordStatus === TransactionConstants.STATUS.PAYMENT_FAILED ||
      landlordStatus === TransactionConstants.STATUS.DEPOSIT_FAILED;
    return (
      <Row>
        <Col className="Dashboard-Price-Badge-Padding txtR">
          <Text type="body" className="Dashboard-Price fs-exclude">
            {centsToDollars(rentAmount)}
          </Text>
          {landlordStatus === TransactionConstants.STATUS.PENDING && (
            <Tag size="sm" className="fs-exclude" tone="gray">
              Pending
            </Tag>
          )}
          {showReturnedBadge && (
            <Tag size="sm" className="fs-exclude" tone="yellow">
              Returned
            </Tag>
          )}
        </Col>
      </Row>
    );
  };

  renderRentalProtectionPremiumTransactions = (): React.ReactNode => {
    const { transaction, listingAlias } = this.props;
    const { rentalProtectionCosts } = transaction;
    return (
      <>
        {transaction.ledgerGroup === TransactionConstants.LEDGER_GROUP.COMPLETED && (
          <ExpansionPanel
            isExpanded={this.state.expandedRentalProtectionCompletedSection}
            onHeaderClick={this.toggleRentalProtectionCompleted}
            header={this.renderCompletedTransactionExpansionHeader()}
            expandedContent={
              <>
                <Row>
                  {this.renderDesktopCols()}
                  {this.renderMobileCols()}
                </Row>
                {rentalProtectionCosts.map((rProCost) => {
                  return (
                    <>
                      <div className="Dashboard-Row-HR" />
                      <RentalProtectionPremiumRow
                        rProTransaction={rProCost}
                        transaction={transaction}
                        listingAlias={listingAlias}
                      />
                    </>
                  );
                })}
              </>
            }
            className="Transaction-expansion-row"
          />
        )}
        {transaction.ledgerGroup !== TransactionConstants.LEDGER_GROUP.COMPLETED &&
          rentalProtectionCosts.map((rProCost, i: number) => {
            return (
              <>
                {i !== 0 && <div className="Dashboard-Row-HR" />}
                <RentalProtectionPremiumRow
                  rProTransaction={rProCost}
                  transaction={transaction}
                  listingAlias={listingAlias}
                />
              </>
            );
          })}
      </>
    );
  };

  renderCompletedTransactionExpansionHeader = (): React.ReactNode => {
    const { landlordStatus, depositAmount, scheduleMonth } = this.props.transaction;
    return (
      <Row className="pbn">
        <Col xs={8} sm={8} md={10} lg={10} xl={10} xxl={10} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col>
              <Text className="fs-exclude" emphasize>
                {formatDate(scheduleMonth, 'MMMM')} Rental Income
              </Text>
            </Col>
          </Row>
        </Col>
        <Col xs={4} sm={4} md={2} lg={2} xl={2} xxl={2} className="paymentDetailsRow-Mobile">
          <Row className="pbn">
            <Col className="Dashboard-Price-Badge-Padding txtR">
              <Text type="body" className="Dashboard-Price fs-exclude">
                {depositAmount && centsToDollars(depositAmount)}
              </Text>
              {landlordStatus === TransactionConstants.STATUS.COMPLETE && (
                <Tag size="sm" className="fs-exclude" tone="green">
                  Paid
                </Tag>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  renderDesktopCols = (): React.ReactNode => (
    <>
      <Col xs={0} sm={0} md={10} lg={10} xl={10} xxl={10}>
        {this.renderLeftColumn()}
      </Col>
      <Col xs={0} sm={0} md={2} lg={2} xl={2} xxl={2}>
        {this.renderRightColumn()}
      </Col>
    </>
  );

  renderMobileCols = (): React.ReactNode => (
    <>
      <Col xs={8} sm={8} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        {this.renderLeftColumn()}
      </Col>
      <Col xs={4} sm={4} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        <Col xs={12} sm={12} md={0} lg={0} xl={0} xxl={0}>
          {this.renderRightColumn()}
        </Col>
      </Col>
    </>
  );

  render(): React.ReactNode {
    const { rentalProtectionCosts } = this.props.transaction;
    if (rentalProtectionCosts?.length > 0) {
      return <>{this.renderRentalProtectionPremiumTransactions()}</>;
    } else {
      return (
        <Row>
          {this.renderDesktopCols()}
          {this.renderMobileCols()}
        </Row>
      );
    }
  }
}
export default RentPaymentsRow;
